import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import { isLexUser } from "@/utils/lexDomain";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getCountryDefaultRate, getVatRatesForCountry } from "@/utils/vatRates";
import UpgradeRequiredView from "@/components/UpgradeRequiredView";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import InvoiceDesignOverride from "@/components/invoices/InvoiceDesignOverride";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Trash2, Repeat, Play, Pause, Edit, StopCircle, Loader2, FileText, TrendingUp, CalendarClock,
} from "lucide-react";

const EUR = (v, currency = "EUR") =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(v || 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("de-DE") : "—");
const toInputDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

const EU_COUNTRIES = new Set(["AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE"]);

const STATUS_META = {
  active: { label: "Aktiv", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  paused: { label: "Pausiert", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  ended: { label: "Beendet", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const INTERVALS = [
  { key: "monthly", label: "Monatlich" },
  { key: "quarterly", label: "Quartalsweise" },
  { key: "half_yearly", label: "Halbjährlich" },
  { key: "yearly", label: "Jährlich" },
  { key: "custom", label: "Individuell" },
];

const SCENARIO_OPTIONS = [
  { key: "standard", label: "Standard (USt ausweisen)" },
  { key: "reverse_charge", label: "Reverse Charge (§13b/§19)" },
  { key: "intra_community", label: "Innergem. Lieferung (§4)" },
  { key: "export", label: "Drittland-Export" },
  { key: "exempt", label: "Steuerfrei" },
];

function isPro(user) {
  if (!user) return false;
  if (user.has_lifetime_access) return true;
  // lex.ivoliq: Anwalts-Accounts haben Honorarnoten immer inklusive
  if (isLexUser(user)) return true;
  return ["pro", "pro_monthly", "pro_yearly", "lifetime"].includes((user.plan || "").toLowerCase());
}

const EMPTY_INLINE = { name: "", email: "", vat_id: "", street: "", postal_code: "", city: "", country_code: "DE" };
const emptyItem = (rate) => ({ description: "", quantity: 1, unit_price: 0, tax_rate: rate, total: 0 });

export default function Recurring() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userCountry = (user?.country || "DE").toUpperCase();

  const [items, setItems] = useState([]);
  const [mrr, setMrr] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [standardArticles, setStandardArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!isPro(user)) { setLoading(false); return; }
    fetchAll();
    if (searchParams.get("new") === "1") { openNew(); setSearchParams({}, { replace: true }); }
  }, []);  // mount only

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, c, p, s] = await Promise.all([
        api.get("/recurring-invoices"),
        api.get("/customers"),
        api.get("/projects"),
        api.get("/standard-articles"),
      ]);
      setItems(r.data?.items || []);
      setMrr(r.data?.mrr_net || 0);
      setActiveCount(r.data?.active_count || 0);
      setCustomers((c.data || []).filter((x) => !x.is_temporary));
      setProjects(p.data || []);
      setStandardArticles(s.data?.articles || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const defaultRate = useMemo(() => getCountryDefaultRate(userCountry), [userCountry]);
  const vatRates = useMemo(() => getVatRatesForCountry(userCountry), [userCountry]);

  const openNew = () => {
    setEditing(null);
    setForm({
      useInline: false,
      customer_id: "",
      inline: { ...EMPTY_INLINE, country_code: userCountry },
      title: "",
      project_id: "",
      items: [emptyItem(getCountryDefaultRate(userCountry))],
      notes: "",
      design_override: null,
      country: userCountry,
      tax_scenario: "standard",
      recurrence_interval: "monthly",
      recurrence_custom_value: 2,
      recurrence_custom_unit: "months",
      anchor_date: toInputDate(new Date()),
      end_mode: "none",
      recurrence_end_date: "",
      max_occurrences: "",
      auto_send: false,
      payment_term_days: 14,
      set_service_period: true,
      include_payment_qr: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      useInline: false,
      customer_id: t.customer_id || "",
      inline: { ...EMPTY_INLINE, country_code: userCountry },
      title: t.title || "",
      project_id: t.project_id || "",
      items: (t.items || []).map((i) => ({ ...i })),
      notes: t.notes || "",
      design_override: t.design_override || null,
      country: t.country || userCountry,
      tax_scenario: t.tax_scenario || "standard",
      recurrence_interval: t.recurrence_interval || "monthly",
      recurrence_custom_value: t.recurrence_custom_value || 2,
      recurrence_custom_unit: t.recurrence_custom_unit || "months",
      anchor_date: toInputDate(t.anchor_date),
      end_mode: t.max_occurrences ? "count" : (t.recurrence_end_date ? "date" : "none"),
      recurrence_end_date: toInputDate(t.recurrence_end_date),
      max_occurrences: t.max_occurrences || "",
      auto_send: !!t.auto_send,
      payment_term_days: t.payment_term_days || 14,
      set_service_period: t.set_service_period !== false,
      include_payment_qr: t.include_payment_qr !== false,
    });
    setDialogOpen(true);
  };

  // ── Item helpers ──
  const setItem = (idx, patch) => {
    setForm((f) => {
      const next = f.items.map((it, i) => {
        if (i !== idx) return it;
        const merged = { ...it, ...patch };
        merged.total = Number(((merged.quantity || 0) * (merged.unit_price || 0)).toFixed(2));
        return merged;
      });
      return { ...f, items: next };
    });
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem(defaultRate)] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const addStandardArticle = (artId) => {
    const a = standardArticles.find((x) => x.id === artId);
    if (!a) return;
    const it = {
      description: a.name + (a.description ? ` – ${a.description}` : ""),
      quantity: 1,
      unit_price: a.unit_price || a.price || 0,
      tax_rate: a.tax_rate ?? defaultRate,
      total: Number((a.unit_price || a.price || 0).toFixed(2)),
    };
    setForm((f) => ({ ...f, items: [...f.items, it] }));
  };

  // ── Cross-border detection ──
  const selectedCustomer = useMemo(() => {
    if (!form) return null;
    return form.useInline ? form.inline : customers.find((c) => c.id === form.customer_id);
  }, [form, customers]);
  const customerCountry = (selectedCustomer?.country_code || "").toUpperCase();
  const isCrossBorder = customerCountry && customerCountry !== userCountry;

  useEffect(() => {
    if (!form || !isCrossBorder) return;
    if (form.tax_scenario !== "standard") return;
    // Auto-Vorschlag bei Cross-Border
    const suggest = EU_COUNTRIES.has(customerCountry) ? "reverse_charge" : "export";
    setForm((f) => ({ ...f, tax_scenario: suggest }));
  }, [customerCountry]);  // re-suggest when customer country changes

  const zeroVat = ["reverse_charge", "intra_community", "export", "exempt"].includes(form?.tax_scenario) || user?.is_kleinunternehmer;
  const totals = useMemo(() => {
    if (!form) return { subtotal: 0, tax: 0, total: 0 };
    const subtotal = (form.items || []).reduce((s, i) => s + (i.total || 0), 0);
    const tax = zeroVat ? 0 : (form.items || []).reduce((s, i) => s + (i.total || 0) * (i.tax_rate || 0) / 100, 0);
    return { subtotal: +subtotal.toFixed(2), tax: +tax.toFixed(2), total: +(subtotal + tax).toFixed(2) };
  }, [form, zeroVat]);

  // ── Save ──
  const save = async () => {
    if (!form) return;
    if (!form.useInline && !form.customer_id) return toast.error("Bitte einen Kunden auswählen.");
    if (form.useInline && !form.inline.name.trim()) return toast.error("Name des Inline-Kunden fehlt.");
    if (!form.items.length || form.items.some((i) => !i.description.trim())) return toast.error("Bitte alle Positionen ausfüllen.");
    if (!form.anchor_date) return toast.error("Bitte ein Startdatum (erste Rechnung) wählen.");

    const payload = {
      title: form.title || null,
      project_id: form.project_id || null,
      items: form.items.map((i) => ({
        description: i.description, quantity: Number(i.quantity) || 0,
        unit_price: Number(i.unit_price) || 0, tax_rate: Number(i.tax_rate) || 0,
        total: Number(i.total) || 0,
      })),
      notes: form.notes || null,
      design_override: form.design_override || null,
      country: form.country || userCountry,
      tax_scenario: form.tax_scenario,
      recurrence_interval: form.recurrence_interval,
      recurrence_custom_value: form.recurrence_interval === "custom" ? (parseInt(form.recurrence_custom_value) || 1) : null,
      recurrence_custom_unit: form.recurrence_interval === "custom" ? (form.recurrence_custom_unit || "months") : null,
      anchor_date: new Date(form.anchor_date).toISOString(),
      recurrence_end_date: form.end_mode === "date" && form.recurrence_end_date ? new Date(form.recurrence_end_date).toISOString() : null,
      max_occurrences: form.end_mode === "count" && form.max_occurrences ? parseInt(form.max_occurrences) : null,
      auto_send: !!form.auto_send,
      payment_term_days: parseInt(form.payment_term_days) || 14,
      set_service_period: !!form.set_service_period,
      include_payment_qr: !!form.include_payment_qr,
    };
    if (form.useInline) payload.inline_customer = form.inline;
    else payload.customer_id = form.customer_id;

    setSaving(true);
    try {
      let resp;
      if (editing) resp = await api.put(`/recurring-invoices/${editing.id}`, payload);
      else resp = await api.post("/recurring-invoices", payload);
      if (!editing) {
        const gen = resp?.data?.generated_count || 0;
        if (gen > 0) {
          toast.success(form.auto_send
            ? "Dauerauftrag aktiviert – erste Rechnung erstellt & versendet."
            : "Dauerauftrag aktiviert – erste Rechnung als Entwurf erstellt.");
        } else {
          toast.success("Dauerauftrag aktiviert – erste Rechnung folgt zum Startdatum.");
        }
      } else {
        toast.success("Dauerauftrag aktualisiert.");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (id, action) => {
    setBusyId(id);
    try {
      const res = await api.post(`/recurring-invoices/${id}/${action}`);
      if (action === "run-now") {
        toast.success(`Rechnung ${res.data?.invoice_number || ""} erstellt (Entwurf).`);
      } else {
        toast.success("Status aktualisiert.");
      }
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/recurring-invoices/${confirmDel.id}`);
      toast.success("Dauerauftrag gelöscht.");
      setConfirmDel(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen.");
      setConfirmDel(null);
    }
  };

  if (!isPro(user)) {
    return (
      <div data-testid="recurring-page">
        <UpgradeRequiredView
          source="recurring" icon={Repeat} title="Retainer & Abos (Wiederkehrende Rechnungen)"
          subtitle="Automatisiere deine Retainer und Abos: Lege eine Vorlage an, und ivoliq erstellt die Rechnungen pünktlich nach Zeitplan — als Entwurf zum Prüfen oder vollautomatisch versendet."
          features={["Rechnungen automatisch nach Zeitplan (monatlich, quartalsweise, jährlich …)", "Entwurf-Modus oder Vollautomatik pro Vorlage", "MRR-Übersicht über deine wiederkehrenden Umsätze", "Nutzt deine bestehende Rechnungs- & Kundenverwaltung"]}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recurring-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Repeat className="h-6 w-6 text-emerald-600" /> Retainer & Abos
          </h1>
          <p className="text-sm text-slate-500 mt-1">Wiederkehrende Rechnungen automatisch nach Zeitplan erstellen.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2" data-testid="recurring-mrr">
            <div className="text-[11px] uppercase tracking-wide text-emerald-700 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> Wiederkehrender Umsatz / Monat
            </div>
            <div className="text-lg font-bold text-emerald-800 tabular-nums">{EUR(mrr)} <span className="text-[11px] font-normal text-emerald-600">netto</span></div>
          </div>
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="recurring-new-btn">
            <Plus className="h-4 w-4 mr-1" /> Neuer Dauerauftrag
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <Repeat className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">Noch keine Daueraufträge</p>
          <p className="text-sm text-slate-400 mt-1 mb-4">Lege deinen ersten Retainer an – ivoliq erstellt die Rechnungen automatisch.</p>
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="recurring-empty-cta">
            <Plus className="h-4 w-4 mr-1" /> Neuer Dauerauftrag
          </Button>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {items.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.active;
            const intLabel = INTERVALS.find((i) => i.key === t.recurrence_interval)?.label || t.recurrence_interval;
            return (
              <Card key={t.id} data-testid={`recurring-card-${t.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 truncate">{t.title || t.customer_name || "Dauerauftrag"}</span>
                        <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
                        <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">{intLabel}</Badge>
                        {t.auto_send
                          ? <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Auto-Versand</Badge>
                          : <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">Entwurf-Modus</Badge>}
                      </div>
                      <div className="text-sm text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        <span>{t.customer_name}</span>
                        <span className="tabular-nums">{EUR(t.total)} <span className="text-slate-400">/ {intLabel.toLowerCase()}</span></span>
                        <span className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Nächste: <strong>{fmtDate(t.next_run_date)}</strong></span>
                        <span>{t.generated_count || 0} Rechnung(en) erstellt</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {t.status === "active" && (
                        <Button size="sm" variant="outline" disabled={busyId === t.id} onClick={() => doAction(t.id, "pause")} data-testid={`recurring-pause-${t.id}`}>
                          <Pause className="h-4 w-4" /><span className="ml-1">Pausieren</span>
                        </Button>
                      )}
                      {t.status === "paused" && (
                        <Button size="sm" variant="outline" disabled={busyId === t.id} onClick={() => doAction(t.id, "resume")} data-testid={`recurring-resume-${t.id}`}>
                          <Play className="h-4 w-4" /><span className="ml-1">Fortsetzen</span>
                        </Button>
                      )}
                      {t.status !== "ended" && (
                        <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`recurring-edit-${t.id}`}>
                          <Edit className="h-4 w-4" /><span className="ml-1">Bearbeiten</span>
                        </Button>
                      )}
                      {(t.generated_count || 0) > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => navigate("/invoices")} data-testid={`recurring-view-invoices-${t.id}`}>
                          <FileText className="h-4 w-4" /><span className="ml-1">Rechnungen</span>
                        </Button>
                      )}
                      {t.status !== "ended" && (
                        <Button size="sm" variant="ghost" className="text-amber-700" disabled={busyId === t.id} onClick={() => doAction(t.id, "end")} data-testid={`recurring-end-${t.id}`}>
                          <StopCircle className="h-4 w-4" /><span className="ml-1">Beenden</span>
                        </Button>
                      )}
                      {(t.generated_count || 0) === 0 && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setConfirmDel(t)} data-testid={`recurring-delete-${t.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="recurring-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Dauerauftrag bearbeiten" : "Neuer Dauerauftrag"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-5">
              {/* Title */}
              <div>
                <Label>Bezeichnung (intern, optional)</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="z.B. Retainer ACME GmbH" data-testid="recurring-title-input" />
              </div>

              {/* Customer */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Kunde</Label>
                  <button type="button" className="text-xs text-emerald-700 hover:underline"
                    onClick={() => setForm({ ...form, useInline: !form.useInline })} data-testid="recurring-toggle-inline">
                    {form.useInline ? "Bestehenden Kunden wählen" : "Neuen Kunden anlegen"}
                  </button>
                </div>
                {form.useInline ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Name *" value={form.inline.name} onChange={(e) => setForm({ ...form, inline: { ...form.inline, name: e.target.value } })} data-testid="recurring-inline-name" />
                    <Input placeholder="E-Mail" value={form.inline.email} onChange={(e) => setForm({ ...form, inline: { ...form.inline, email: e.target.value } })} />
                    <Input placeholder="USt-IdNr." value={form.inline.vat_id} onChange={(e) => setForm({ ...form, inline: { ...form.inline, vat_id: e.target.value } })} />
                    <Input list="recurring-countries" placeholder="Land (ISO, z.B. DE)" value={form.inline.country_code}
                      onChange={(e) => setForm({ ...form, inline: { ...form.inline, country_code: e.target.value.toUpperCase() } })} />
                    <datalist id="recurring-countries">
                      {["DE","AT","CH","FR","IT","NL","ES","PL","US","GB"].map((c) => <option key={c} value={c} />)}
                    </datalist>
                  </div>
                ) : (
                  <select className="w-full h-10 rounded-md border border-slate-300 px-2" value={form.customer_id}
                    onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="recurring-customer-select">
                    <option value="">— Kunde wählen —</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Positionen</Label>
                  {standardArticles.length > 0 && (
                    <select className="h-8 text-xs rounded-md border border-slate-300 px-2" value=""
                      onChange={(e) => { if (e.target.value) addStandardArticle(e.target.value); e.target.value = ""; }} data-testid="recurring-standard-article">
                      <option value="">+ Standardartikel</option>
                      {standardArticles.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  )}
                </div>
                <div className="space-y-2">
                  {form.items.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`recurring-item-${idx}`}>
                      <Input className="col-span-5" placeholder="Beschreibung" value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })} />
                      <Input className="col-span-1" type="number" placeholder="Menge" value={it.quantity} onChange={(e) => setItem(idx, { quantity: parseFloat(e.target.value) || 0 })} />
                      <Input className="col-span-2" type="number" placeholder="Preis" value={it.unit_price} onChange={(e) => setItem(idx, { unit_price: parseFloat(e.target.value) || 0 })} />
                      <select className="col-span-2 h-10 rounded-md border border-slate-300 px-1 text-sm" value={it.tax_rate} onChange={(e) => setItem(idx, { tax_rate: parseFloat(e.target.value) })}>
                        {vatRates.map((r) => <option key={r.rate} value={r.rate}>{r.label}</option>)}
                      </select>
                      <div className="col-span-1 text-sm text-right tabular-nums">{EUR(it.total)}</div>
                      <button className="col-span-1 text-red-500 flex justify-center" onClick={() => removeItem(idx)} disabled={form.items.length === 1}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" className="mt-2" onClick={addItem} data-testid="recurring-add-item">
                  <Plus className="h-4 w-4 mr-1" /> Position
                </Button>
                <div className="mt-3 flex justify-end gap-6 text-sm">
                  <span className="text-slate-500">Netto: <strong className="tabular-nums">{EUR(totals.subtotal)}</strong></span>
                  <span className="text-slate-500">USt: <strong className="tabular-nums">{EUR(totals.tax)}</strong></span>
                  <span className="text-slate-900 font-semibold">Gesamt: <span className="tabular-nums">{EUR(totals.total)}</span></span>
                </div>
              </div>

              {/* Tax scenario (cross-border) */}
              {(isCrossBorder || form.tax_scenario !== "standard") && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="recurring-tax-scheme-card">
                  <Label className="text-blue-900">Steuerart {isCrossBorder && <span className="text-xs font-normal">(Cross-Border erkannt: {customerCountry})</span>}</Label>
                  <select className="w-full h-10 rounded-md border border-blue-300 px-2 mt-1 bg-white" value={form.tax_scenario}
                    onChange={(e) => setForm({ ...form, tax_scenario: e.target.value })} data-testid="recurring-tax-scenario-select">
                    {SCENARIO_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
              )}

              {/* Recurrence */}
              <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                <Label className="flex items-center gap-2"><Repeat className="h-4 w-4 text-emerald-600" /> Wiederholung</Label>
                <div className="flex flex-wrap gap-1.5">
                  {INTERVALS.map(({ key, label }) => (
                    <button key={key} type="button" onClick={() => setForm({ ...form, recurrence_interval: key })}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${form.recurrence_interval === key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                      data-testid={`recurring-interval-${key}`}>{label}</button>
                  ))}
                </div>
                {form.recurrence_interval === "custom" && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-600">alle</span>
                    <Input type="number" min={1} className="h-8 w-16" value={form.recurrence_custom_value}
                      onChange={(e) => setForm({ ...form, recurrence_custom_value: Math.max(1, parseInt(e.target.value) || 1) })} />
                    <select className="h-8 rounded-md border border-slate-300 px-2 text-sm" value={form.recurrence_custom_unit}
                      onChange={(e) => setForm({ ...form, recurrence_custom_unit: e.target.value })}>
                      <option value="weeks">Wochen</option>
                      <option value="months">Monate</option>
                    </select>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Startdatum (erste Rechnung)</Label>
                    <Input type="date" value={form.anchor_date} onChange={(e) => setForm({ ...form, anchor_date: e.target.value })} data-testid="recurring-anchor-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Laufzeit-Ende</Label>
                    <select className="w-full h-10 rounded-md border border-slate-300 px-2" value={form.end_mode}
                      onChange={(e) => setForm({ ...form, end_mode: e.target.value })} data-testid="recurring-end-mode">
                      <option value="none">Unbegrenzt</option>
                      <option value="date">Enddatum</option>
                      <option value="count">Nach N Rechnungen</option>
                    </select>
                  </div>
                </div>
                {form.end_mode === "date" && (
                  <Input type="date" value={form.recurrence_end_date} onChange={(e) => setForm({ ...form, recurrence_end_date: e.target.value })} data-testid="recurring-end-date" />
                )}
                {form.end_mode === "count" && (
                  <Input type="number" min={1} placeholder="z.B. 12" value={form.max_occurrences}
                    onChange={(e) => setForm({ ...form, max_occurrences: e.target.value })} data-testid="recurring-max-occ" />
                )}
              </div>

              {/* Mode + payment term */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 p-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.auto_send} onChange={(e) => setForm({ ...form, auto_send: e.target.checked })} className="h-4 w-4" data-testid="recurring-autosend-toggle" />
                    <span className="text-sm font-medium text-slate-800">Vollautomatik (automatisch versenden)</span>
                  </label>
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    {form.auto_send
                      ? "Rechnung wird erstellt UND per E-Mail an den Kunden versendet. Du bist für die Richtigkeit verantwortlich."
                      : "Entwurf-Modus: Rechnung wird als Entwurf erstellt – du prüfst und sendest sie selbst."}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <Label className="text-xs">Zahlungsziel (Tage)</Label>
                  <Input type="number" min={0} value={form.payment_term_days} onChange={(e) => setForm({ ...form, payment_term_days: e.target.value })} data-testid="recurring-payment-term" />
                  <label className="flex items-center gap-2 cursor-pointer mt-2">
                    <input type="checkbox" checked={form.set_service_period} onChange={(e) => setForm({ ...form, set_service_period: e.target.checked })} className="h-4 w-4" />
                    <span className="text-xs text-slate-600">Leistungszeitraum je Periode automatisch setzen</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer mt-2">
                    <input type="checkbox" checked={form.include_payment_qr} onChange={(e) => setForm({ ...form, include_payment_qr: e.target.checked })} className="h-4 w-4" data-testid="recurring-qr-toggle" />
                    <span className="text-xs text-slate-600">Zahlungs-QR-Code (GiroCode/EPC) auf der Rechnung</span>
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label>Notizen (optional)</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                <div className="mt-3">
                  <InvoiceDesignOverride value={form.design_override} onChange={(v) => setForm({ ...form, design_override: v })} locked={false} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} disabled={saving} data-testid="recurring-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {editing ? "Speichern" : "Dauerauftrag anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        isOpen={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        title="Dauerauftrag löschen?"
        description="Dieser Dauerauftrag hat noch keine Rechnungen erzeugt und kann gelöscht werden."
        confirmText="Löschen"
        onConfirm={remove}
      />
    </div>
  );
}
